import React, { useCallback, useMemo } from 'react'
import { TransitionablePortal } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import type { AutomationTarget, SlottableComponent } from '../../../types'
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics'
import useBreakpoints from '../../../hooks/useBreakpoints'
import Icon from '../../elementals/icon'
import Animate from '../../elementals/animate'
import CardContainer from '../../elementals/cardContainer'
import { ColorNames } from '../../../constants/colorVars'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const MAX_HEADER_HEIGHT_FOR_SMALL_PADDING = 95
const DEFAULT_COLLAPSED_HEIGHT = 160
const DEFAULT_EXPANDED_HEIGHT = 580

type heightHints = {
  collapsed: number // corresponds to the height of the card when collapsed
  expanded: number // corresponds to the height of the card when expanded
}
interface IconWrapperProps {
  onClick: () => void
  active: boolean
  fullPageExpandOnMobile: boolean
  className?: string
  style?: React.CSSProperties
  isMobile?: boolean
}

const IconWrapper: React.FC<IconWrapperProps> = ({
  onClick,
  active,
  fullPageExpandOnMobile,
  className,
  style = {},
  isMobile = false,
}) => {
  return (
    <Icon
      onClick={onClick}
      iconStyle='light'
      color='gray'
      size={isMobile ? 'small' : 'large'}
      name={
        fullPageExpandOnMobile && isMobile ? 'arrow-up-right' : 'angle-left'
      }
      className={cx('icon', { active }, className)}
      data-elm-id='toggle_expandable_card'
      style={style}
    />
  )
}
interface ContentWrapperProps {
  active: boolean
  transitionDurationMs: number
  content: React.ReactNode
  contentHeight: number
  className?: string
}

const ContentWrapper: React.FC<ContentWrapperProps> = ({
  active,
  transitionDurationMs,
  content,
  contentHeight,
  className,
}) => (
  // super-deluxe height + opacity transition!
  <Animate.FadeInOutWithHeight
    isVisible={active}
    duration={transitionDurationMs / 2}
    height={contentHeight}
    contentAdjustment={24}
    style={{ overflow: 'hidden' }}
  >
    <div className={cx('content', { active }, className)}>{content}</div>
  </Animate.FadeInOutWithHeight>
)

/**
 * renderDefaultCard will render a default card if a card slot is not provided.
 */
interface renderDefaultCardProps extends AutomationTarget {
  children: React.ReactNode
  className?: string
  isMobile?: boolean
  borderColor?: ColorNames
  borderStyle?: 'none' | 'line' | 'gradient' | 'unset'
}

const renderDefaultCard = ({
  children,
  className,
  elmId = 'card',
  isMobile = false,
  borderColor,
  borderStyle,
}: renderDefaultCardProps) => (
  <CardContainer
    elmId={elmId}
    borderRadius='extra-small'
    dropShadow={isMobile ? 'small' : 'medium'}
    padding={isMobile ? 'none' : 'medium'}
    className={className}
    borderColor={borderColor}
    borderStyle={borderStyle}
  >
    {children}
  </CardContainer>
)

type ExpandableCardClassNames = {
  card?: string
  title?: string
  content?: string
  icon?: string
}

/**
 * ExpandableCardProps
 * Expects slots for card, header, and content, however card slot is optional (will render a default card if not provided).
 */
export interface ExpandableCardProps
  extends SlottableComponent<'card' | 'header' | 'content'>,
    AutomationTarget {
  id?: string | number
  onClick?: (id: string | number) => void
  active: boolean
  heightHints?: heightHints
  bottomMargin?: number
  borderColor?: ColorNames
  borderStyle?: 'none' | 'line' | 'gradient' | 'unset'
  transitionDurationMs?: number
  hideHeaderWhenExpanded?: boolean
  fullPageExpandOnMobile?: boolean
  classNames?: ExpandableCardClassNames
  disableExpansion?: boolean
}

/**
 * ExpandableCard is a card that can be expanded or collapsed.
 * When used in a list of cards e.g. RankList, heightHints should be set to the height of the card in the
 * collapsed and expanded states.  This isn't mandatory, but it will make the transition smoother.
 */
const ExpandableCard: React.FunctionComponent<ExpandableCardProps> = ({
  elmId,
  id = 0,
  slots,
  active,
  onClick,
  heightHints = {
    collapsed: DEFAULT_COLLAPSED_HEIGHT, // default collapsed height, doesn't have to be precise
    expanded: DEFAULT_EXPANDED_HEIGHT, // default expanded height, doesn't have to be precise
  },
  bottomMargin = 18,
  borderColor,
  borderStyle,
  transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS as number,
  hideHeaderWhenExpanded = false,
  fullPageExpandOnMobile = false,
  classNames = {},
  disableExpansion = false,
}) => {
  const breakpoint = useBreakpoints()
  const isMobile = breakpoint === 'mobile'
  const hasCardSlot = Boolean(slots?.card?.())

  const isSmallHeader =
    heightHints.collapsed < MAX_HEADER_HEIGHT_FOR_SMALL_PADDING

  // contentHeight should be height of content only - not fully expanded height
  const contentHeight = useMemo(
    () =>
      hideHeaderWhenExpanded
        ? heightHints.expanded // content equals expanded height when header is hidden
        : heightHints.expanded - heightHints.collapsed,
    [heightHints.collapsed, heightHints.expanded, hideHeaderWhenExpanded]
  )

  const iconPaddingClass = isSmallHeader ? 'small-pad' : 'medium-pad'

  /**
   * forceCollapsed is used to conditionally force the card to be collapsed when opening the portal version
   * (i.e. when using fullPageExpandOnMobile) so that the underlying card doesn't expand when the portal is opened.
   */
  const renderContent = useCallback(
    (forceCollapsed: boolean) =>
      !hideHeaderWhenExpanded ? (
        <>
          <div
            className={cx('title', classNames.title, {
              active: active && !forceCollapsed,
            })}
          >
            {slots?.header?.()}
            {onClick && (
              <IconWrapper
                onClick={() => onClick?.(id)}
                active={active && !forceCollapsed}
                className={cx(classNames.icon, iconPaddingClass, {
                  flip: fullPageExpandOnMobile && isMobile,
                })}
                fullPageExpandOnMobile={fullPageExpandOnMobile}
                isMobile={isMobile}
              />
            )}
          </div>
          <ContentWrapper
            active={active && !forceCollapsed}
            transitionDurationMs={transitionDurationMs}
            content={slots?.content?.()}
            contentHeight={contentHeight}
            className={cx(classNames.icon)}
          />
          {onClick && (
            <div
              data-elm-id={`click-area-${id}`}
              className={cx('click-area', iconPaddingClass)}
              onClick={() => onClick?.(id)}
            />
          )}
        </>
      ) : (
        <>
          {onClick && (
            <IconWrapper
              onClick={() => onClick?.(id)}
              active={active && !forceCollapsed}
              className={cx(classNames.icon, iconPaddingClass, {
                flip: fullPageExpandOnMobile && isMobile,
              })}
              fullPageExpandOnMobile={fullPageExpandOnMobile}
            />
          )}
          <div className={cx('overlap')}>
            <Animate.FadeInOut
              isVisible={!active || forceCollapsed}
              duration={transitionDurationMs / 2}
            >
              <div className={cx('title', classNames.title)}>
                {slots?.header?.()}
              </div>
            </Animate.FadeInOut>
          </div>
          <div className={cx('overlap')}>
            <ContentWrapper
              active={active && !forceCollapsed}
              transitionDurationMs={transitionDurationMs}
              content={slots?.content?.()}
              contentHeight={contentHeight}
              className={classNames.content}
            />
          </div>
          {onClick && (
            <div
              data-elm-id={`click-area-${id}`}
              className={cx('click-area', iconPaddingClass)}
              onClick={() => !disableExpansion && onClick?.(id)}
            />
          )}
        </>
      ),
    [
      active,
      borderColor,
      borderStyle,
      classNames.content,
      classNames.icon,
      classNames.title,
      contentHeight,
      fullPageExpandOnMobile,
      hideHeaderWhenExpanded,
      iconPaddingClass,
      id,
      isMobile,
      onClick,
      slots,
      transitionDurationMs,
      disableExpansion,
    ]
  )

  const getPartialCardProps = useCallback(
    (forceCollapsed: boolean) => {
      return {
        elmId,
        children: renderContent(forceCollapsed),
        className: cx(
          'card',
          {
            'hiding-header': hideHeaderWhenExpanded,
            'full-screen':
              fullPageExpandOnMobile && !forceCollapsed && isMobile,
          },
          classNames.card
        ),
        isMobile,
        borderColor,
        borderStyle,
      }
    },
    [
      borderColor,
      borderStyle,
      classNames.card,
      elmId,
      fullPageExpandOnMobile,
      hideHeaderWhenExpanded,
      isMobile,
      renderContent,
    ]
  )

  return (
    <>
      <div
        /**
         * This magic set's the height immediately to hinted values to kick in animation of height change,
         * which reduces the delay when inside RankList.  We use minHeight on active to "push" it open without
         * restricting the height (as it may be larger).  On inactive, we use height to "pull" it closed.
         *
         * Note that heights will auto-compensate for discrepancies between hint and actual height, via the
         * FadeInOutWithHeight animation.
         */
        style={
          active && !(fullPageExpandOnMobile && isMobile)
            ? {
                minHeight: heightHints.expanded,
                marginBottom: bottomMargin,
              }
            : {
                height: heightHints.collapsed,
                marginBottom: bottomMargin,
              }
        }
      >
        {!hasCardSlot &&
          renderDefaultCard(
            getPartialCardProps(fullPageExpandOnMobile && isMobile)
          )}
        {hasCardSlot &&
          slots?.card?.(
            getPartialCardProps(fullPageExpandOnMobile && isMobile)
          )}
      </div>
      {/**
       * If fullPageExpandOnMobile is true, we render the card in a portal so that it can expand to full screen on mobile.
       */}
      {fullPageExpandOnMobile && isMobile && (
        <TransitionablePortal
          open={active}
          transition={{ animation: 'fade up', duration: 500 }}
        >
          <div className={cx('portal')}>
            {!hasCardSlot && renderDefaultCard(getPartialCardProps(false))}
            {hasCardSlot && slots?.card?.(getPartialCardProps(false))}
          </div>
        </TransitionablePortal>
      )}
    </>
  )
}

export default ExpandableCard
